import React, { useState, useEffect, useRef } from 'react';
import { Upload, Button, Progress, Card, message, List, Tag, Typography, Statistic, Row, Col, Divider, Alert, Spin } from 'antd';
import { UploadOutlined, InboxOutlined, CloudUploadOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import axios from 'axios';
import VirtualList from 'rc-virtual-list';

const { Dragger } = Upload;
const { Title, Text } = Typography;

// File list constants
const FILE_LIST_HEIGHT = 400;
const FILE_ITEM_HEIGHT = 100;
const MAX_FILES_PER_BATCH = 25;

// Upload status constants with lighter colors
const UPLOAD_STATUS = {
  PENDING: { text: 'Pending', color: '#faad14', bgColor: '#fff8e6' },  // Light yellow
  UPLOADING: { text: 'Uploading', color: '#1890ff', bgColor: '#e6f7ff' },  // Light blue
  SUCCESS: { text: 'Success', color: '#52c41a', bgColor: '#f6ffed' },  // Light green
  ERROR: { text: 'Error', color: '#f5222d', bgColor: '#fff1f0' }  // Light red
};

// Server URL for uploads - dynamically set based on current location
const SERVER_URL = window.location.origin;

// localStorage key for upload statistics
const STORAGE_KEY = 'file_upload_stats';

// Constants for file uploads
const UPLOAD_TIMEOUT = 24 * 60 * 60 * 1000; // 24-hour timeout (in milliseconds)

// Add a session management utility
const SessionManager = {
  // Get session from localStorage with validation
  getStoredSession: () => {
    try {
      const storedId = localStorage.getItem('upload_session_id');
      // Check if it matches UUID v4 format
      if (storedId && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(storedId)) {
        return storedId;
      } else if (storedId) {
        console.warn('Found invalid stored session, will request new one', storedId);
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
    }
    return null;
  },
  
  // Store session ID safely
  storeSession: (sessionId) => {
    try {
      if (sessionId) {
        localStorage.setItem('upload_session_id', sessionId);
      }
    } catch (error) {
      console.error('Failed to store session ID:', error);
    }
  },
  
  // Clear session 
  clearSession: () => {
    try {
      localStorage.removeItem('upload_session_id');
    } catch (error) {
      console.error('Failed to clear session ID:', error);
    }
  }
};

// Get initial stats from localStorage
const getInitialStats = () => {
  try {
    const storedStats = localStorage.getItem(STORAGE_KEY);
    if (storedStats) {
      return JSON.parse(storedStats);
    }
  } catch (error) {
    console.error('Error retrieving stats from localStorage:', error);
  }
  
  // Default values if nothing in localStorage or error occurs
  return {
    totalUploaded: 0,
    successCount: 0,
    failCount: 0,
    lastUploadDate: null
  };
};

// Handle file upload in batches to prevent UI freezing
const processBatch = (allFiles, callback, setBatchProgress, batchSize = MAX_FILES_PER_BATCH) => {
  let currentIndex = 0;
  
  // Initialize the batch progress
  setBatchProgress({ 
    active: true, 
    current: 0, 
    total: allFiles.length 
  });

  const processNextBatch = () => {
    const batch = allFiles.slice(currentIndex, currentIndex + batchSize);
    if (batch.length === 0) {
      setBatchProgress({ active: false, current: 0, total: 0 });
      return;
    }

    // Process this batch
    batch.forEach(callback);
    
    // Move to next batch
    currentIndex += batchSize;
    
    // Update progress
    setBatchProgress(prev => ({
      ...prev,
      current: Math.min(currentIndex, prev.total)
    }));
    
    // Schedule next batch with a small delay to allow UI to update
    if (currentIndex < allFiles.length) {
      setTimeout(processNextBatch, 50);
    } else {
      // Reset progress when all files have been processed
      setTimeout(() => setBatchProgress({ active: false, current: 0, total: 0 }), 500);
    }
  };

  processNextBatch();
};

// Add a constant for connection polling at the top with the other constants
const CONNECTION_POLL_INTERVAL = 30000; // 30 seconds instead of 10 seconds
const MAX_INITIAL_RETRIES = 3; // Limit initial connection attempts
const RETRY_BACKOFF_FACTOR = 2; // Exponential backoff factor

const FileUploader = () => {
  const [fileList, setFileList] = useState([]);
  const [uploadStats, setUploadStats] = useState(getInitialStats);
  const [uploading, setUploading] = useState(false);
  const [maxConcurrentUploads, setMaxConcurrentUploads] = useState(3);
  const [sessionId, setSessionId] = useState(null);
  const activeUploadsRef = useRef(0); // Track number of active uploads
  const uploadQueueRef = useRef([]); // Queue for files waiting to be uploaded
  const [timerTick, setTimerTick] = useState(0); // For forcing timer updates
  const [globalActiveUploads, setGlobalActiveUploads] = useState(0); // Track global active uploads
  const [serverWarning, setServerWarning] = useState(null); // Display server warnings
  const [sessionStatus, setSessionStatus] = useState('initializing'); // Track session status
  const sessionRetryCountRef = useRef(0); // Track session connection retries
  const [lastConnectionAttempt, setLastConnectionAttempt] = useState(0); // Track last attempt time

  // Handle large file lists with debouncing 
  const [processingFiles, setProcessingFiles] = useState(false);
  
  // Add state for tracking batch processing progress
  const [batchProgress, setBatchProgress] = useState({ active: false, current: 0, total: 0 });

  // Function to set up Axios defaults with the session ID
  const configureAxiosDefaults = (sid) => {
    if (sid) {
      axios.defaults.headers.common['X-Session-Id'] = sid;
    } else {
      delete axios.defaults.headers.common['X-Session-Id'];
    }
  };

  // Function to establish or recover session
  const establishSession = async (forceNew = false) => {
    // Don't retry more than once per second - prevents excessive requests
    const now = Date.now();
    if (now - lastConnectionAttempt < 1000) {
      console.log('Throttling connection attempts - too frequent');
      return;
    }
    setLastConnectionAttempt(now);
    
    let existingSessionId = forceNew ? null : SessionManager.getStoredSession();
    
    try {
      setSessionStatus('connecting');
      
      // Configure request headers based on whether we have an existing session
      const headers = existingSessionId ? { 'X-Session-Id': existingSessionId } : {};
      
      const response = await axios.head('/api/upload', { headers });
      
      // Get session ID from response
      const newSessionId = response.headers['x-session-id'];
      
      if (newSessionId) {
        
        // Store the new session ID
        setSessionId(newSessionId);
        SessionManager.storeSession(newSessionId);
        
        // Configure Axios defaults
        configureAxiosDefaults(newSessionId);
        
        // Update session status
        setSessionStatus('connected');
        sessionRetryCountRef.current = 0;
        
        // Get max concurrent uploads
        const maxUploads = parseInt(response.headers['x-max-concurrent-uploads'], 10);
        if (!isNaN(maxUploads)) {
          setMaxConcurrentUploads(maxUploads);
        }
        
        // Get global active uploads
        const globalActive = parseInt(response.headers['x-global-active'], 10);
        if (!isNaN(globalActive)) {
          setGlobalActiveUploads(globalActive);
        }
        
        // Clear any warning
        if (serverWarning) {
          setServerWarning(null);
        }
      } else {
        throw new Error('No session ID returned from server');
      }
    } catch (error) {
      console.error('Session establishment failed:', error);
      
      // Update session status
      setSessionStatus('error');
      
      // Implement exponential backoff for retries
      const retryCount = sessionRetryCountRef.current;
      
      // If we've tried with an existing session ID and failed, try with a new one
      if (existingSessionId && retryCount < MAX_INITIAL_RETRIES) {
        console.log(`Session recovery attempt ${retryCount + 1}/${MAX_INITIAL_RETRIES}`);
        sessionRetryCountRef.current++;
        
        // Use exponential backoff (1s, 2s, 4s, etc.)
        const backoffDelay = 1000 * Math.pow(RETRY_BACKOFF_FACTOR, retryCount);
        setTimeout(() => establishSession(true), backoffDelay);
      } else if (retryCount < MAX_INITIAL_RETRIES) {
        // Limited retries for complete failures
        console.log(`New connection attempt ${retryCount + 1}/${MAX_INITIAL_RETRIES}`);
        sessionRetryCountRef.current++;
        
        // Use exponential backoff (2s, 4s, 8s, etc.)
        const backoffDelay = 2000 * Math.pow(RETRY_BACKOFF_FACTOR, retryCount);
        setTimeout(() => establishSession(false), backoffDelay);
      } else {
        // Give up after too many retries
        setServerWarning('Could not establish a session with the server after multiple attempts. Please refresh the page or try again later.');
      }
    }
  };

  // Session recovery function for when uploads fail with session errors
  const recoverSession = async () => {
    setServerWarning('Session recovery in progress...');
    
    // Clear existing session
    setSessionId(null);
    SessionManager.clearSession();
    configureAxiosDefaults(null);
    
    // Establish a completely new session
    await establishSession(true);
    
    setServerWarning('Session recovered. You can retry your uploads now.');
  };

  // Get session ID and max concurrent uploads from server on component mount
  useEffect(() => {
    // Initial session establishment
    establishSession();
    
    // Set up polling interval for server status - now with longer interval
    const intervalId = setInterval(async () => {
      // Only poll if we have an established session
      if (sessionStatus === 'connected' && sessionId) {
        try {
          const response = await axios.head('/api/upload');
          
          // Check if the server still recognizes our session
          const returnedSessionId = response.headers['x-session-id'];
          if (returnedSessionId !== sessionId) {
            console.warn('Session ID mismatch in status poll:', returnedSessionId, 'vs', sessionId);
            recoverSession();
            return;
          }
          
          // Update global active uploads
          const globalActive = parseInt(response.headers['x-global-active'], 10);
          if (!isNaN(globalActive)) {
            setGlobalActiveUploads(globalActive);
          }
          
          // Clear any warning if connection is restored
          if (serverWarning) {
            setServerWarning(null);
          }
        } catch (error) {
          console.warn('Failed to poll server status:', error);
          
          // If we can't connect multiple times, try to recover the session
          if (sessionRetryCountRef.current > 2) {
            recoverSession();
          } else {
            sessionRetryCountRef.current++;
          }
        }
      }
    }, CONNECTION_POLL_INTERVAL); // Increased from 10s to 30s to reduce requests
    
    return () => clearInterval(intervalId);
  }, [sessionId, sessionStatus, serverWarning]);

  // Update localStorage when upload stats change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(uploadStats));
    } catch (error) {
      console.error('Error saving stats to localStorage:', error);
    }
  }, [uploadStats]);

  // Update timer every second for active uploads
  useEffect(() => {
    let timerId;
    const hasActiveUploads = fileList.some(file => 
      file.status?.text === 'Uploading'
    );

    if (hasActiveUploads) {
      timerId = setInterval(() => {
        setFileList(prevList => prevList.map(file => {
          if (file.status?.text === 'Uploading') {
            return {
              ...file,
              status: {
                ...file.status,
                elapsedTime: Date.now() - file.status.startTime
              }
            };
          }
          return file;
        }));
      }, 1000);
    }

    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [fileList]);

  // Handle file upload with progress
  const uploadFile = async (file) => {
    // Skip if session is not established
    if (sessionStatus !== 'connected' || !sessionId) {
      console.error('Cannot upload: no active session');
      updateFileStatus(file.uid, {
        text: 'No session - retry later',
        type: 'error',
        color: '#f5222d',
        bgColor: '#fff1f0'
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file.originFileObj);

    try {
      // Update file status to start tracking time immediately
      updateFileStatus(file.uid, {
        text: 'Uploading',
        type: 'info',
        percent: 0,
        startTime: Date.now(),
        elapsedTime: 0
      });

      const response = await axios.post('/api/upload', formData, {
        timeout: UPLOAD_TIMEOUT,
        headers: {
          'Content-Type': 'multipart/form-data'
          // Session ID is in axios defaults
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const currentTime = Date.now();
            updateFileStatus(file.uid, {
              text: 'Uploading',
              type: 'info',
              percent,
              elapsedTime: currentTime - (file.status?.startTime || currentTime)
            });
          }
        },
      });

      const endTime = Date.now();
      const duration = endTime - (file.status?.startTime || endTime);

      // Update final status while preserving the duration and timing information
      updateFileStatus(file.uid, {
        text: 'Success',
        type: 'success',
        color: '#52c41a',
        bgColor: '#f6ffed',
        percent: 100,
        duration,
        endTime,
        elapsedTime: duration
      });

      // Update statistics with upload speed
      const fileSizeInMB = file.size / (1024 * 1024);
      const durationInSeconds = duration / 1000;
      const speedMBps = fileSizeInMB / durationInSeconds;
      setUploadStats(prev => ({
        ...prev,
        successCount: prev.successCount + 1
      }));

      // Update global active uploads count after successful upload
      try {
        const statusResponse = await axios.head('/api/upload');
        const globalActive = parseInt(statusResponse.headers['x-global-active'], 10);
        if (!isNaN(globalActive)) {
          setGlobalActiveUploads(globalActive);
        }
      } catch (error) {
        console.warn('Could not get updated server status:', error);
      }

    } catch (error) {
      console.error('Upload error:', error);
      const endTime = Date.now();
      const duration = endTime - (file.status?.startTime || file.startTime || endTime);
      
      let errorMessage = 'Upload failed';
      let needSessionRecovery = false;
      
      if (error.response) {
        // Handle different HTTP error responses
        if (error.response.status === 429) {
          errorMessage = 'Too many concurrent uploads. Please wait.';
        } else if (error.response.status === 503) {
          errorMessage = 'Server overloaded. Try again later.';
          // Show global warning for server overload
          setServerWarning('The server is currently handling too many uploads. Please try again later.');
        } else if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = 'Session invalid or expired.';
          needSessionRecovery = true;
        } else {
          errorMessage = error.response.data.error || 'Server error';
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timed out after 24 hours';
      } else if (!error.response) {
        errorMessage = 'Network error - check your connection';
        // Show global warning for network issues
        setServerWarning('Network connection issues detected. Please check your internet connection.');
        
        // Increment the retry counter if this seems to be a persistent issue
        sessionRetryCountRef.current++;
        
        // If we've had multiple failures, try to recover the session
        if (sessionRetryCountRef.current > 2) {
          needSessionRecovery = true;
        }
      }

      // Update error status while preserving timing information
      updateFileStatus(file.uid, {
        text: errorMessage,
        type: 'error',
        color: '#f5222d',
        bgColor: '#fff1f0',
        duration,
        endTime,
        startTime: file.status?.startTime || file.startTime
      });

      setUploadStats(prev => ({
        ...prev,
        failCount: prev.failCount + 1
      }));
      
      // If we have session issues, try to recover
      if (needSessionRecovery) {
        recoverSession();
      }
    }
  };

  // Format duration in a human-readable format
  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
  };

  // Process the upload queue - enhanced version to ensure parallel uploads
  const processUploadQueue = async () => {
    
    if (uploadQueueRef.current.length === 0) {
      // If there are no active uploads and the queue is empty, we can mark uploading as complete
      if (activeUploadsRef.current === 0) {
        setUploading(false);
      }
      return;
    }
    
    // Calculate how many new uploads we can start
    const availableSlots = maxConcurrentUploads - activeUploadsRef.current;
    
    if (availableSlots <= 0) {
      return;
    }
    
    // Start as many uploads as we can (up to availableSlots)
    const filesToUpload = uploadQueueRef.current.splice(0, availableSlots);
    
    // Start each upload in parallel
    filesToUpload.forEach(file => {
      activeUploadsRef.current++;
      
      // Use Promise to handle upload completion
      uploadFile(file)
        .catch(err => console.error(`Upload error for ${file.name}:`, err))
        .finally(() => {
          activeUploadsRef.current--;
          
          // After each upload completes, check if we can process more files
          setTimeout(() => {
            processUploadQueue();
          }, 100);
        });
    });
  };

  // Upload all files
  const uploadAll = async () => {
    setUploading(true);
    
    try {
      // Get all pending files that aren't already uploading, successful, or failed
      const pendingFiles = fileList.filter(file => 
        !file.status || (file.status.text !== 'Uploading' && file.status.text !== 'Success' && file.status.text !== 'Error')
      );

      // Reset and fill the upload queue
      uploadQueueRef.current = [...pendingFiles];
      
      // Start processing the queue
      processUploadQueue();
    } catch (error) {
      console.error('Upload process error:', error);
      setUploading(false);
    }
  };

  const updateFileProgress = (uid, percent) => {
    setFileList(prevList => 
      prevList.map(item => 
        item.uid === uid ? { ...item, percent } : item
      )
    );
  };

  const updateFileStatus = (uid, newStatus) => {
    setFileList(prevList => 
      prevList.map(item => 
        item.uid === uid 
          ? { 
              ...item, 
              status: {
                ...(item.status || {}),  // Preserve existing status properties
                ...newStatus  // Apply new status properties
              },
              percent: newStatus.percent !== undefined ? newStatus.percent : item.percent
            } 
          : item
      )
    );
  };

  const customRequest = ({ file, onSuccess }) => {
    // Just add the file to the list without uploading
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const handleChange = ({ fileList: newFileList }) => {
    // Only show one notification for large batches, not for each file
    if (newFileList.length > MAX_FILES_PER_BATCH && !processingFiles && !batchProgress.active) {
      setProcessingFiles(true);
      
      // First, add files to list with minimal processing
      const existingFiles = fileList.reduce((acc, file) => {
        acc[file.uid] = file;
        return acc;
      }, {});
      
      // Process in batches with setTimeout to avoid UI freeze
      processBatch(newFileList, (file) => {
        // Preserve existing file status if it exists
        const existingFile = existingFiles[file.uid];
        
        setFileList(prevList => [
          ...prevList.filter(f => f.uid !== file.uid),
          {
            ...file,
            status: existingFile?.status || undefined,
            percent: existingFile?.percent || 0
          }
        ]);
      }, setBatchProgress);
      
      // Reset processing flag when done (after all batches will be processed)
      setTimeout(() => setProcessingFiles(false), 
                 Math.ceil(newFileList.length / MAX_FILES_PER_BATCH) * 100);
      
      return;
    }
    
    // For smaller file lists, use the original approach
    const updatedFileList = newFileList.map(file => {
      const existingFile = fileList.find(f => f.uid === file.uid);
      if (existingFile) {
        return {
          ...file,
          status: existingFile.status,
          percent: existingFile.percent
        };
      }
      // For new files, don't set any status yet
      return {
        ...file,
        percent: 0
      };
    });
    
    setFileList(updatedFileList);
  };

  // Limit max files for drag operations to prevent browser slowdown
  const beforeUpload = (file, fileList) => {
    // Only show notification for first file in large batch
    if (fileList.length > 50 && fileList[0] === file) {
      message.info(`Processing ${fileList.length} files. This may take a moment.`);
    }
    
    return true;
  };

  const uploadProps = {
    name: 'files',
    multiple: true,
    fileList,
    customRequest,
    onChange: handleChange,
    showUploadList: false, // We'll handle our own list rendering
    beforeUpload
  };

  // Remove file from list
  const removeFile = (uid) => {
    setFileList(prevList => prevList.filter(file => file.uid !== uid));
  };

  // Clear all finished uploads (success or error)
  const clearFinished = () => {
    setFileList(prevList => prevList.filter(file => 
      !file.status || (file.status.text !== 'Success' && file.status.text !== 'Error')
    ));
  };

  // Reset statistics
  const resetStats = () => {
    if (window.confirm('Are you sure you want to reset upload statistics? This cannot be undone.')) {
      const resetData = {
        totalUploaded: 0,
        successCount: 0,
        failCount: 0,
        lastUploadDate: null
      };
      
      setUploadStats(resetData);
      
      // Ensure localStorage is updated immediately
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(resetData));
        message.success('Upload statistics have been reset');
      } catch (error) {
        console.error('Error resetting stats in localStorage:', error);
        message.error('Failed to reset statistics');
      }
    }
  };

  // Get elapsed time for a file that's currently uploading
  const getElapsedTime = (file) => {
    if (!file.status?.startTime) return 0;
    
    if (file.status?.text === 'Uploading') {
      // For active uploads, use the cached elapsed time or calculate fresh
      return file.status.elapsedTime || Date.now() - file.status.startTime;
    } else if (file.status?.endTime) {
      // For completed uploads, use the final duration
      return file.status.duration;
    }
    
    return 0;
  };

  return (
    <div>
      {/* Fixed Alert component that doesn't mount/unmount - prevents flickering */}
      <div style={{ height: '76px', marginBottom: '20px' }}>
        <div style={{ 
          opacity: sessionStatus !== 'connected' ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          position: 'absolute',
          width: 'calc(100% - 48px)', // Account for padding
          zIndex: sessionStatus !== 'connected' ? 1 : -1
        }}>
          <Alert
            message={sessionStatus === 'initializing' ? "Initializing" : 
                    sessionStatus === 'connecting' ? "Connecting to server..." : 
                    "Session Error"}
            description={sessionStatus === 'error' ? 
                        "Could not establish a connection to the server. Retrying..." : 
                        "Establishing secure connection to the server..."}
            type={sessionStatus === 'error' ? "error" : "info"}
            showIcon
          />
        </div>
      </div>
      
      {/* Global progress for file processing */}
      {batchProgress.active && (
        <Card style={{ marginBottom: '20px', borderLeft: '4px solid #1890ff' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: '15px' }}>
              <Spin />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                <Text>Processing files ({batchProgress.current} of {batchProgress.total})</Text>
                <Text>{Math.round((batchProgress.current / batchProgress.total) * 100)}%</Text>
              </div>
              <Progress 
                percent={Math.round((batchProgress.current / batchProgress.total) * 100)} 
                status="active" 
                showInfo={false}
              />
            </div>
          </div>
        </Card>
      )}
      
      {/* Server status warning */}
      {serverWarning && (
        <Alert
          message="Server Warning"
          description={serverWarning}
          type="warning"
          showIcon
          closable
          onClose={() => setServerWarning(null)}
          style={{ marginBottom: '20px' }}
          action={
            sessionStatus === 'error' ? 
            <Button 
              size="small" 
              type="primary" 
              onClick={() => recoverSession()}>
              Reset Session
            </Button> : null
          }
        />
      )}
      
      {/* Server load indicator */}
      {globalActiveUploads > 0 && (
        <Card 
          style={{ 
            marginBottom: '20px', 
            backgroundColor: globalActiveUploads > 7 ? '#fff1f0' : globalActiveUploads > 4 ? '#fffbe6' : '#f6ffed',
            borderLeft: `4px solid ${globalActiveUploads > 7 ? '#f5222d' : globalActiveUploads > 4 ? '#faad14' : '#52c41a'}`
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: '12px', fontSize: '20px' }}>
              {globalActiveUploads > 7 ? <WarningOutlined style={{ color: '#f5222d' }} /> : 
               globalActiveUploads > 4 ? <WarningOutlined style={{ color: '#faad14' }} /> : 
               <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            </div>
            <div>
              <Text strong>Server Load:</Text>
              <Text> Currently processing {globalActiveUploads} active uploads. </Text>
              {globalActiveUploads > 7 && <Text type="danger">High server load - expect delays.</Text>}
              {globalActiveUploads > 4 && globalActiveUploads <= 7 && <Text type="warning">Moderate server load.</Text>}
              {globalActiveUploads <= 4 && <Text type="success">Server load is optimal.</Text>}
            </div>
          </div>
        </Card>
      )}

      {/* File naming guidance message - moved to top */}
      <Card 
        style={{ 
          marginBottom: '20px', 
          backgroundColor: '#fffbe6', 
          borderLeft: '4px solid #faad14' 
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginRight: '12px', fontSize: '20px', color: '#faad14' }}>
            ℹ️
          </div>
          <div>
            <Text strong>Important:</Text>
            <Text> Please try your best to rename the file into </Text>
            <Text code>XAAAAAA-DDDBBBBBB-00000000-00000000.zip</Text>
            <Text> format before uploading.</Text>
          </div>
        </div>
      </Card>

      <Card className="stats-card" style={{ marginBottom: '20px', textAlign: 'center' }}>
        <Row gutter={16} justify="center">
          <Col span={6}>
            <Statistic 
              title="Total Attempts" 
              value={uploadStats.successCount + uploadStats.failCount} 
              prefix={<CloudUploadOutlined />} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Success" 
              value={uploadStats.successCount} 
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />} 
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Fail" 
              value={uploadStats.failCount} 
              valueStyle={{ color: '#f5222d' }}
              prefix={<CloseCircleOutlined />} 
            />
          </Col>
        </Row>
        
        <div style={{ marginTop: '16px' }}>
          <Button size="small" onClick={resetStats} danger>Reset Statistics</Button>
        </div>
      </Card>

      <Card className="upload-card">
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag files to this area to select files</p>
          <p className="ant-upload-hint">
            Support for multiple file uploads. Click the Upload All button to start uploading.
          </p>
        </Dragger>
      </Card>

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <Button 
          type="primary" 
          onClick={uploadAll} 
          loading={uploading}
          icon={<CloudUploadOutlined />}
          size="large"
          disabled={fileList.length === 0}
          style={{ marginRight: '10px' }}
        >
          Upload All Files
        </Button>
        <Button
          onClick={clearFinished}
          icon={<CloseCircleOutlined />}
          size="large"
          disabled={!fileList.some(file => 
            file.status && (file.status.text === 'Success' || file.status.text === 'Error')
          )}
        >
          Clear Finished
        </Button>
      </div>

      <div className="upload-list">
        {fileList.length > 0 && (
          <Card title={`${fileList.length} File${fileList.length > 1 ? 's' : ''}`} style={{ marginBottom: 20 }}>
            <List>
              <VirtualList
                data={fileList}
                height={Math.min(FILE_LIST_HEIGHT, fileList.length * FILE_ITEM_HEIGHT)}
                itemHeight={FILE_ITEM_HEIGHT}
                itemKey="uid"
              >
                {(file) => (
                  <List.Item key={file.uid}>
                    <Card 
                      style={{ 
                        width: '100%', 
                        backgroundColor: file.status ? file.status.bgColor : '#f9f9f9',
                        borderLeft: `4px solid ${file.status ? file.status.color : '#d9d9d9'}`
                      }}
                      hoverable
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{file.name}</Text>
                          <div>
                            <Tag color={file.status ? file.status.color : 'default'}>
                              {file.status ? file.status.text : 'New'}
                            </Tag>
                            <Text type="secondary">{(file.size / 1024 / 1024).toFixed(2)} MB</Text>
                            {file.duration && (
                              <Tag color="blue" style={{ marginLeft: '8px' }}>
                                Time: {formatDuration(file.duration)}
                              </Tag>
                            )}
                            {!file.status && (
                              <Button 
                                type="text" 
                                size="small" 
                                danger 
                                onClick={() => removeFile(file.uid)}
                                style={{ marginLeft: '8px' }}
                              >
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                        {file.status && file.status.text === 'Uploading' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {/* Real-time upload timer */}
                            <div>
                              <Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
                                Elapsed
                              </Text>
                              <Tag color="processing" style={{ margin: 0 }}>
                                {formatDuration(getElapsedTime(file))}
                              </Tag>
                            </div>
                            
                            {/* Progress circle */}
                            <Progress 
                              type="circle" 
                              percent={file.percent || 0} 
                              width={50}
                            />
                          </div>
                        )}
                        {file.status && file.status.text === 'Success' && (
                          <div style={{ color: '#52c41a', fontSize: '24px' }}>✓</div>
                        )}
                        {file.status && file.status.text === 'Error' && (
                          <div style={{ color: '#f5222d', fontSize: '24px' }}>✗</div>
                        )}
                      </div>
                    </Card>
                  </List.Item>
                )}
              </VirtualList>
            </List>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FileUploader; 